/**
 * The DCF mail tab (UM-R5, Isaiah Cotto 2026-10-02).
 *
 * Every message DCF sent to a DCF login eMerge generated, newest first: what
 * it is (NOCA, a notice posted at DCF, an account verification, a password
 * message, other), whose it is -- CounselEAR id and initials here; the name
 * is on the case -- and what happened to it. A NOCA's PDF is already filed on
 * the case ("NOCA on file · not yet reviewed"); recording the review stays on
 * the case, a person's act. Mail no login matches is listed too, for a person
 * to place: nothing is dropped.
 *
 * The server decides scope and every capability; this only renders what it
 * was given.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { dcfMailApi, shortDateTime, DCF_MAIL_CLASS_LABEL, DCF_MAIL_CLASSES } from './umedApi';
import type { DcfMailPage, DcfMailClass, DcfMailState } from './umedApi';
import { DcfMailMessage, residentLine, classLine } from './DcfMailParts';
import './DcfMail.css';

const STATE_LABEL: Record<DcfMailState, string> = { open: 'Open', handled: 'Handled', all: 'All' };

export default function DcfMailView({ q, page, selected, onOpenCase, onPage }: {
  q: string; page: number; selected: string | null; onOpenCase: (caseId: string) => void; onPage: (p: number) => void;
}) {
  const [state, setState] = useState<DcfMailState>('open');
  const [cls, setCls] = useState<DcfMailClass | null>(null);
  const [data, setData] = useState<DcfMailPage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await dcfMailApi.list({ state, cls, q, page });
      if (r.success && r.data) setData(r.data);
      else setErr(r.code === 'AUTHZ_DENY_NO_CAPABILITY' || r.status === 403 ? 'You do not have access to DCF mail.' : (r.error || 'DCF mail could not be loaded.'));
    } catch { setErr('Could not reach the server.'); } finally { setLoading(false); }
  }, [state, cls, q, page]);
  useEffect(() => { load(); }, [load]);

  const choose = (next: { state?: DcfMailState; cls?: DcfMailClass | null }) => {
    if (next.state !== undefined) setState(next.state);
    if (next.cls !== undefined) setCls(next.cls);
    setExpanded(null);
    if (page !== 1) onPage(1);
  };

  const openCount = data ? Object.values(data.open_by_class).reduce((a, b) => a + (b || 0), 0) : null;
  const last = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <section className="um-card" aria-label="DCF mail">
      <div className="um-card-head">
        <div>
          <h2>DCF mail</h2>
          <p className="why">What DCF sent to the residents' DCF logins. A NOCA's file is filed on its case as evidence; recording the NOCA as received is still done on the case.</p>
        </div>
        {openCount !== null && <span className="um-count" title="Open messages in your scope">{openCount}</span>}
      </div>

      {data && data.ingest === 'OFF' && (
        <div className="um-note" role="status"><span><strong>Not switched on here.</strong> eMerge is not reading DCF mail on this server yet; anything listed was read earlier.</span></div>
      )}
      {notice && <p className="um-notice" role="status">{notice}</p>}

      <div className="um-dcfmail-filters">
        <div className="um-lenses" role="group" aria-label="Which messages">
          {(['open', 'handled', 'all'] as DcfMailState[]).map((s) => (
            <button key={s} type="button" className={`um-lens${state === s ? ' on' : ''}`} aria-pressed={state === s} onClick={() => choose({ state: s })}>{STATE_LABEL[s]}</button>
          ))}
        </div>
        <div className="um-lenses" role="group" aria-label="Kind of message">
          <button type="button" className={`um-lens${cls === null ? ' on' : ''}`} aria-pressed={cls === null} onClick={() => choose({ cls: null })}>Every kind</button>
          {DCF_MAIL_CLASSES.map((c) => (
            <button key={c} type="button" className={`um-lens${cls === c ? ' on' : ''}`} aria-pressed={cls === c} onClick={() => choose({ cls: c })}>
              {DCF_MAIL_CLASS_LABEL[c]}{data && data.open_by_class[c] ? <span className="um-count" title="Open">{data.open_by_class[c]}</span> : null}
            </button>
          ))}
        </div>
      </div>

      {err && <p className="um-notice stop" role="alert">{err}</p>}
      {!err && loading && !data && <p className="um-quiet" role="status">Loading DCF mail…</p>}
      {!err && data && data.items.length === 0 && (
        <p className="um-empty">{state === 'open' ? 'No open DCF mail in your scope.' : 'No DCF mail in your scope matches.'}</p>
      )}
      {!err && data && data.items.length > 0 && (
        <ul className="um-rows" aria-label="DCF mail messages">
          {data.items.map((m) => (
            <li key={m.id} className={`um-dcfmail-row${m.case_id && m.case_id === selected ? ' on' : ''}`}>
              <div className="main">
                <span className="um-quiet">{shortDateTime(m.received_at)}</span>
                <strong>{classLine(m)}</strong>
                <div className="why">{residentLine(m)}</div>
                {m.handled && <div className="um-quiet">Handled {shortDateTime(m.handled.at)}{m.handled.by_name ? ` by ${m.handled.by_name}` : ''}{m.handled.reason ? ` · ${m.handled.reason}` : ''}</div>}
              </div>
              <div className="btns">
                {m.case_id && <button type="button" className="um-link" onClick={() => onOpenCase(m.case_id as string)}>Open case</button>}
                <button type="button" className="um-link" aria-expanded={expanded === m.id} onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
                  {expanded === m.id ? 'Hide' : 'Show message'}
                </button>
              </div>
              {expanded === m.id && (
                <DcfMailMessage item={m} canOpen={data.can.open} canMark={data.can.mark_handled}
                  onChanged={async (msg) => { setNotice(msg); await load(); }} />
              )}
            </li>
          ))}
        </ul>
      )}
      {data && data.total > data.limit && (
        <div className="um-pager">
          <span className="num">{(data.page - 1) * data.limit + 1}–{Math.min(data.page * data.limit, data.total)} of {data.total}</span>
          <div className="btns">
            <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1}>Previous</button>
            <button type="button" onClick={() => onPage(page + 1)} disabled={page >= last}>Next</button>
          </div>
        </div>
      )}
    </section>
  );
}
